export const MAX_VOICE_SECONDS = 60;
export const VOICE_SAMPLE_RATE = 16000;

// Deterministic PCM WAV, not a WebM blob renamed to .mp3. The backend validates
// these exact headers and measures actual samples before any paid request.
export function encodeVoiceWav(chunks, sourceRate) {
  if (!Number.isFinite(sourceRate) || sourceRate < 8000 || sourceRate > 192000) throw new Error("voice_invalid_audio");
  const size = Math.min(chunks.reduce((n, c) => n + c.length, 0), Math.floor(sourceRate * MAX_VOICE_SECONDS));
  const source = new Float32Array(size); let at = 0;
  for (const chunk of chunks) { const count = Math.min(chunk.length, size - at); source.set(chunk.subarray(0, count), at); at += count; if (at >= size) break; }
  const length = Math.min(Math.floor(size / sourceRate * VOICE_SAMPLE_RATE), MAX_VOICE_SECONDS * VOICE_SAMPLE_RATE);
  if (length < VOICE_SAMPLE_RATE * 0.3) throw new Error("voice_no_speech");
  const bytes = new ArrayBuffer(44 + length * 2), view = new DataView(bytes);
  const ascii = (offset, text) => { [...text].forEach((char, i) => view.setUint8(offset + i, char.charCodeAt(0))); };
  ascii(0, "RIFF"); view.setUint32(4, 36 + length * 2, true); ascii(8, "WAVE"); ascii(12, "fmt ");
  view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, 1, true);
  view.setUint32(24, VOICE_SAMPLE_RATE, true); view.setUint32(28, VOICE_SAMPLE_RATE * 2, true);
  view.setUint16(32, 2, true); view.setUint16(34, 16, true); ascii(36, "data"); view.setUint32(40, length * 2, true);
  const step = sourceRate / VOICE_SAMPLE_RATE;
  for (let i = 0; i < length; i++) {
    const offset = i * step, left = Math.floor(offset), fraction = offset - left;
    const value = Math.max(-1, Math.min(1, source[left] * (1 - fraction) + (source[Math.min(left + 1, size - 1)] || 0) * fraction));
    view.setInt16(44 + i * 2, value < 0 ? value * 32768 : value * 32767, true);
  }
  return new Blob([bytes], { type: "audio/wav" });
}

export async function beginVoiceRecording(onSamples) {
  if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) throw new Error("voice_unsupported");
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass || !window.AudioWorkletNode) throw new Error("voice_unsupported");
  let stream, context, source, node, silent;
  const stop = () => {
    if (node) { node.port.onmessage = null; node.disconnect(); }
    source?.disconnect(); silent?.disconnect();
    stream?.getTracks().forEach((track) => track.stop());
    context?.close().catch(() => {});
  };
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true }, video: false });
    try { context = new AudioContextClass({ sampleRate: VOICE_SAMPLE_RATE }); }
    catch { context = new AudioContextClass(); } // devices with a fixed native rate
    await context.audioWorklet.addModule(`${process.env.PUBLIC_URL || ""}/assistant-recorder-worklet.js`);
    source = context.createMediaStreamSource(stream);
    node = new AudioWorkletNode(context, "smartfarm-recorder", { numberOfInputs: 1, numberOfOutputs: 1, channelCount: 1 });
    silent = context.createGain(); silent.gain.value = 0;
    node.port.onmessage = (event) => { if (event.data instanceof Float32Array) onSamples(event.data, context.sampleRate); };
    source.connect(node); node.connect(silent); silent.connect(context.destination); await context.resume();
    return { stop, sampleRate: context.sampleRate };
  } catch (error) { stop(); throw error; }
}
